import { useState } from 'react';
import { useCategories, useCreateCategory, useUpdateCategory, useDeleteCategory, useRecalculateListPrices } from '@/hooks/useProducts';
import { ProductCategory, PriceFormula } from '@/types';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import { Plus, Edit, Trash2, Tag, Calculator, Percent, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';

interface CategoryFormData {
  name: string;
  description: string;
  price_formula: PriceFormula;
  price_multiplier: number;
  margin_percentage: number;
  price_round_to: number;
  list_price_multiplier: number;
}

const FORMULA_OPTIONS: { value: PriceFormula; label: string; description: string }[] = [
  { value: 'multiply_round_up', label: 'Multiplicador (redondeado)', description: 'Costo × multiplicador, redondeado al múltiplo superior' },
  { value: 'multiply_exact', label: 'Multiplicador (exacto)', description: 'Costo × multiplicador, sin redondeo' },
  { value: 'margin_percentage', label: 'Porcentaje de margen', description: 'Costo + porcentaje%, redondeado al múltiplo más cercano' },
  { value: 'fixed_margin', label: 'Margen fijo', description: 'Costo + monto fijo' },
];

const DEFAULT_FORM_DATA: CategoryFormData = {
  name: '',
  description: '',
  price_formula: 'multiply_round_up',
  price_multiplier: 2.0,
  margin_percentage: 30.0,
  price_round_to: 100,
  list_price_multiplier: 1.10,
};

export default function Categories() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ProductCategory | null>(null);
  const [formData, setFormData] = useState<CategoryFormData>(DEFAULT_FORM_DATA);
  const [deleteConfirm, setDeleteConfirm] = useState<ProductCategory | null>(null);

  const { data: categories, isLoading } = useCategories();
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();
  const recalculateListPrices = useRecalculateListPrices();
  // Recalcular pisa los precios de tarjeta de TODA la categoria y no se puede deshacer,
  // y el boton esta al lado de editar: se tocaba por error. Ahora pide confirmacion.
  const [recalculateConfirm, setRecalculateConfirm] = useState<ProductCategory | null>(null);

  const handleRecalculate = async (categoryId: number) => {
    try {
      const result = await recalculateListPrices.mutateAsync(categoryId);
      toast.success(`Se actualizaron ${result.updated_count} productos`);
    } catch {
      toast.error('Error al recalcular precios');
    } finally {
      setRecalculateConfirm(null);
    }
  };

  const handleOpenModal = (category?: ProductCategory) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description || '',
        price_formula: category.price_formula || 'multiply_round_up',
        price_multiplier: category.price_multiplier || 2.0,
        margin_percentage: category.margin_percentage || 30.0,
        price_round_to: category.price_round_to || 100,
        list_price_multiplier: category.list_price_multiplier || 1.10,
      });
    } else {
      setEditingCategory(null);
      setFormData(DEFAULT_FORM_DATA);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCategory(null);
    setFormData(DEFAULT_FORM_DATA);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const categoryData = {
        name: formData.name,
        description: formData.description || undefined,
        price_formula: formData.price_formula,
        price_multiplier: formData.price_multiplier,
        margin_percentage: formData.margin_percentage,
        price_round_to: formData.price_round_to,
        list_price_multiplier: formData.list_price_multiplier,
      };

      if (editingCategory) {
        const multiplierChanged = formData.list_price_multiplier !== (editingCategory.list_price_multiplier || 1.10);
        await updateCategory.mutateAsync({
          id: editingCategory.id,
          data: categoryData,
        });
        // Auto-recalculate card prices if multiplier changed
        if (multiplierChanged) {
          await handleRecalculate(editingCategory.id);
        }
      } else {
        await createCategory.mutateAsync(categoryData);
      }
      handleCloseModal();
    } catch (error) {
      console.error('Error saving category:', error);
    }
  };

  // Calcula precio de ejemplo basado en la fórmula seleccionada
  const calculateExamplePrice = (cost: number): number => {
    const { price_formula, price_multiplier, margin_percentage, price_round_to } = formData;

    if (price_formula === 'multiply_round_up') {
      const multiplied = cost * price_multiplier;
      return Math.ceil(multiplied / price_round_to) * price_round_to;
    } else if (price_formula === 'multiply_exact') {
      return Math.round(cost * price_multiplier * 100) / 100;
    } else if (price_formula === 'margin_percentage') {
      const margin = cost * margin_percentage / 100;
      const priceWithMargin = cost + margin;
      return Math.round(priceWithMargin / price_round_to) * price_round_to;
    } else if (price_formula === 'fixed_margin') {
      return Math.round((cost + price_multiplier) * 100) / 100;
    }
    return cost * 2;
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteCategory.mutateAsync(deleteConfirm.id);
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const isSubmitting = createCategory.isPending || updateCategory.isPending;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Categorías</h1>
          <p className="text-gray-600">Gestiona las categorías de productos</p>
        </div>
        <Button onClick={() => handleOpenModal()}>
          <Plus className="h-4 w-4 mr-2" />
          Nueva Categoría
        </Button>
      </div>

      <div className="card">
        {isLoading ? (
          <div className="text-center py-8">Cargando...</div>
        ) : categories && categories.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {categories.map((category) => {
              const formulaDetail = category.price_formula === 'margin_percentage'
                ? `${category.margin_percentage || 30}%`
                : category.price_formula === 'fixed_margin'
                ? `+$${category.price_multiplier || 500}`
                : `×${category.price_multiplier || 2}`;

              return (
                <div
                  key={category.id}
                  className="flex items-center justify-between py-4 px-2 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-taupe-100 rounded-lg flex items-center justify-center">
                      <Tag className="h-5 w-5 text-taupe-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{category.name}</h3>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        {category.description && (
                          <>
                            <span>{category.description}</span>
                            <span className="text-gray-300">|</span>
                          </>
                        )}
                        <span className="inline-flex items-center gap-1 text-taupe-600">
                          <Calculator className="h-3 w-3" />
                          {formulaDetail}
                        </span>
                        <span className="text-gray-300">|</span>
                        <span className="inline-flex items-center gap-1 text-blue-600">
                          💳 +{Math.round(((category.list_price_multiplier ?? 1.10) - 1) * 100)}%
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setRecalculateConfirm(category)}
                      disabled={recalculateListPrices.isPending}
                      title="Recalcular precios de tarjeta"
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenModal(category)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteConfirm(category)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No hay categorías creadas
          </div>
        )}
      </div>

      {/* Modal de crear/editar */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Nombre *"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Ej: Vacunas, Medicamentos..."
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Descripción
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all"
              placeholder="Descripción opcional..."
            />
          </div>

          {/* Configuración de fórmula de precios */}
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center gap-2 mb-3">
              <Calculator className="h-4 w-4 text-taupe-600" />
              <h3 className="font-medium text-gray-900">Fórmula de Precios</h3>
            </div>

            <div className="space-y-3">
              {/* Selector de fórmula */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Tipo de cálculo
                </label>
                <select
                  value={formData.price_formula}
                  onChange={(e) => setFormData({ ...formData, price_formula: e.target.value as PriceFormula })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all"
                >
                  {FORMULA_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {FORMULA_OPTIONS.find(o => o.value === formData.price_formula)?.description}
                </p>
              </div>

              {/* Campos según la fórmula seleccionada */}
              <div className="grid grid-cols-2 gap-3">
                {/* Multiplicador - mostrar para multiply_* y fixed_margin */}
                {(formData.price_formula === 'multiply_round_up' || formData.price_formula === 'multiply_exact') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Multiplicador
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="1"
                      value={formData.price_multiplier}
                      onChange={(e) => setFormData({ ...formData, price_multiplier: parseFloat(e.target.value) || 2 })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all"
                    />
                    <p className="text-xs text-gray-500 mt-1">Ej: 2.0 = costo × 2</p>
                  </div>
                )}

                {/* Porcentaje de margen - solo para margin_percentage */}
                {formData.price_formula === 'margin_percentage' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Porcentaje de margen
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="1"
                        min="0"
                        value={formData.margin_percentage}
                        onChange={(e) => setFormData({ ...formData, margin_percentage: parseFloat(e.target.value) || 30 })}
                        className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all"
                      />
                      <Percent className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Ej: 30 = costo + 30%</p>
                  </div>
                )}

                {/* Margen fijo - solo para fixed_margin */}
                {formData.price_formula === 'fixed_margin' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Margen fijo ($)
                    </label>
                    <input
                      type="number"
                      step="100"
                      min="0"
                      value={formData.price_multiplier}
                      onChange={(e) => setFormData({ ...formData, price_multiplier: parseFloat(e.target.value) || 500 })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all"
                    />
                    <p className="text-xs text-gray-500 mt-1">Monto fijo a sumar al costo</p>
                  </div>
                )}

                {/* Redondear al múltiplo - para multiply_round_up y margin_percentage */}
                {(formData.price_formula === 'multiply_round_up' || formData.price_formula === 'margin_percentage') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Redondear a múltiplo de
                    </label>
                    <select
                      value={formData.price_round_to}
                      onChange={(e) => setFormData({ ...formData, price_round_to: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all"
                    >
                      <option value={10}>$10</option>
                      <option value={50}>$50</option>
                      <option value={100}>$100</option>
                      <option value={500}>$500</option>
                      <option value={1000}>$1.000</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Precio tarjeta */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Recargo precio tarjeta
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    step="0.01"
                    min="1"
                    value={formData.list_price_multiplier}
                    onChange={(e) => setFormData({ ...formData, list_price_multiplier: parseFloat(e.target.value) || 1.10 })}
                    className="w-32 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none transition-all"
                  />
                  <span className="text-sm text-gray-600">
                    = +{Math.round((formData.list_price_multiplier - 1) * 100)}% sobre precio efectivo
                  </span>
                </div>
                {editingCategory && (
                  <button
                    type="button"
                    onClick={async () => {
                      // Save category first so backend uses the new multiplier
                      const categoryData = {
                        name: formData.name,
                        description: formData.description || undefined,
                        price_formula: formData.price_formula,
                        price_multiplier: formData.price_multiplier,
                        margin_percentage: formData.margin_percentage,
                        price_round_to: formData.price_round_to,
                        list_price_multiplier: formData.list_price_multiplier,
                      };
                      await updateCategory.mutateAsync({
                        id: editingCategory.id,
                        data: categoryData,
                      });
                      await handleRecalculate(editingCategory.id);
                    }}
                    disabled={recalculateListPrices.isPending || isSubmitting}
                    className="mt-2 inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${recalculateListPrices.isPending ? 'animate-spin' : ''}`} />
                    {recalculateListPrices.isPending ? 'Recalculando...' : 'Guardar y aplicar a todos los productos'}
                  </button>
                )}
              </div>

              {/* Ejemplo de cálculo */}
              <div className="bg-taupe-50 rounded-lg p-3 mt-3">
                <p className="text-sm font-medium text-taupe-800 mb-2">Ejemplo de cálculo:</p>
                <div className="text-sm text-taupe-600 space-y-1">
                  <p>Costo: $1.000</p>
                  <p>Precio efectivo: <span className="font-semibold text-taupe-800">${calculateExamplePrice(1000).toLocaleString('es-AR')}</span></p>
                  <p>Precio tarjeta: <span className="font-semibold text-taupe-800">${Math.round(calculateExamplePrice(1000) * formData.list_price_multiplier).toLocaleString('es-AR')}</span></p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="secondary" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button type="submit" loading={isSubmitting}>
              {editingCategory ? 'Guardar' : 'Crear'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal de confirmación de eliminación */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Eliminar Categoría"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            ¿Estás seguro de que deseas eliminar la categoría{' '}
            <strong>{deleteConfirm?.name}</strong>?
          </p>
          <p className="text-sm text-gray-500">
            Los productos asociados a esta categoría quedarán sin categoría asignada.
          </p>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              loading={deleteCategory.isPending}
            >
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Confirmar recálculo de precios de tarjeta */}
      <Modal
        isOpen={!!recalculateConfirm}
        onClose={() => setRecalculateConfirm(null)}
        title="Recalcular precios de tarjeta"
      >
        <div className="space-y-4">
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="font-medium text-amber-800">
              ¿Recalcular los precios de tarjeta de {recalculateConfirm?.name}?
            </p>
            <p className="text-sm text-amber-700 mt-1">
              Se va a pisar el precio de tarjeta de <strong>todos los productos</strong> de esta
              categoría, aplicándoles el recargo de{' '}
              {recalculateConfirm?.list_price_multiplier
                ? `${Math.round((Number(recalculateConfirm.list_price_multiplier) - 1) * 100)}%`
                : 'la categoría'}{' '}
              sobre el precio de efectivo. Los precios de tarjeta cargados a mano se pierden y la
              acción no se puede deshacer.
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setRecalculateConfirm(null)}>
              Cancelar
            </Button>
            <Button
              className="flex-1"
              onClick={() => recalculateConfirm && handleRecalculate(recalculateConfirm.id)}
              loading={recalculateListPrices.isPending}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Sí, recalcular
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
